/* This file is maintained by LLMs.

This is the abstract base class for all database adapters that provide two-way synchronization 
between SQLite and Prisma-defined PostgreSQL tables.

The adapter follows an architecture where data is synchronized to a common format,
then synced to the respective databases.

IMPORTANT: This system uses a SPLIT ARCHITECTURE with separate client and server adapters:

- CLIENT ADAPTER: Runs in the desktop app, syncs between SQLite and common item format
- SERVER ADAPTER: Runs on the server, syncs between Prisma and common item format  
- REST LAYER: Connects client and server adapters through HTTP API calls

This split allows each adapter to be instantiated in its respective environment without
cross-dependencies (client can't access Prisma, server can't access SQLite).

Each adapter must implement the abstract methods defined in this base class.

*/

import type { ServerAdapterType } from '@repo/common';
import type {
  AdapterConfig,
  ServerDatabaseAdapterInterface,
  WithSomeRequired
} from '@repo/common/types/Adapter';
import type { SyncDirection, SyncItemsInput } from '@repo/common/types/AdapterSync';
import type { BaseModel } from '@repo/common/types/Sqlite';
import { processBatchesWithConcurrency } from '@repo/toolslib';
import { ValidationUtils } from '@repo/toolslib/src/utils/validation.util';
import type { HeavyFieldConfig } from './heavyField.config';
import { getHeavyFieldStorageHandlers } from './heavyField.storage';
import type { CommonItem, DeduplicationStrategy, MinimalItem } from './types';
import { convertNullToUndefined } from './utils';

// Common sync direction type and interfaces are now imported from './types'

// ─── CUSTOM ERROR TYPES ───

/**
 * Custom error for foreign key constraint violations
 * Used to distinguish between different types of database errors
 */
export class ForeignKeyConstraintError extends Error {
  public readonly code = 'FOREIGN_KEY_CONSTRAINT';
  public readonly dependentTable: string;
  public readonly dependentId: string;
  public readonly originalError: Error;

  constructor(message: string, dependentTable: string, dependentId: string, originalError: Error) {
    super(message);
    this.name = 'ForeignKeyConstraintError';
    this.dependentTable = dependentTable;
    this.dependentId = dependentId;
    this.originalError = originalError;
  }

  /**
   * Serialize error for API response
   */
  toJSON() {
    return {
      code: this.code,
      message: this.message,
      dependentTable: this.dependentTable,
      dependentId: this.dependentId,
      originalError: this.originalError.message
    };
  }
}

/**
 * Check if an error is a Prisma foreign key constraint error
 */
export function isForeignKeyConstraintError(error: unknown): boolean {
  const err = error as { code?: string; meta?: { cause?: string; field_name?: string } };

  // P2003: Foreign key constraint failed on the field
  if (err?.code === 'P2003') {
    return true;
  }

  // P2025: Record not found (can be related to foreign key issues with nested connects)
  if (
    err?.code === 'P2025' &&
    err?.meta?.cause?.includes('nested connect') &&
    err?.meta?.cause?.includes('relation')
  ) {
    return true;
  }

  return false;
}

/**
 * Extract foreign key constraint details from Prisma error
 */
export function extractForeignKeyConstraintDetails(error: unknown): {
  dependentTable: string;
  dependentId: string;
} | null {
  if (!isForeignKeyConstraintError(error)) {
    return null;
  }

  const err = error as { code?: string; meta?: { cause?: string; field_name?: string } };

  // Handle P2003: Foreign key constraint failed on the field
  if (err?.code === 'P2003') {
    const fieldName = err.meta?.field_name || 'unknown';
    // Try to extract table name from field name (e.g., "workspaceId" -> "Workspace")
    const dependentTable = fieldName.replace(/Id$/, '').replace(/^./, (char) => char.toUpperCase());
    return { dependentTable, dependentId: 'unknown' };
  }

  // Handle P2025: Record not found (nested connect)
  if (err?.code === 'P2025') {
    const cause = err.meta?.cause || '';
    // Example: "No 'Memory' record(s) (needed to inline the relation on 'Task' record(s)) was found for a nested connect on one-to-many relation 'MemoryToTask'."
    const tableMatch = cause.match(/No '([^']+)' record/);
    const dependentTable = tableMatch ? tableMatch[1] : 'unknown';
    return { dependentTable, dependentId: 'unknown' };
  }

  return { dependentTable: 'unknown', dependentId: 'unknown' };
}

/**
 * Custom error for unique constraint violations
 * Used to distinguish between different types of database errors
 */
export class UniqueConstraintError extends Error {
  public readonly code = 'UNIQUE_CONSTRAINT';
  public readonly constraintFields: string[];
  public readonly originalError: Error;

  constructor(message: string, constraintFields: string[], originalError: Error) {
    super(message);
    this.name = 'UniqueConstraintError';
    this.constraintFields = constraintFields;
    this.originalError = originalError;
  }

  /**
   * Serialize error for API response
   */
  toJSON() {
    return {
      code: this.code,
      message: this.message,
      constraintFields: this.constraintFields,
      originalError: this.originalError.message
    };
  }
}

/**
 * Check if an error is a Prisma unique constraint error
 */
export function isUniqueConstraintError(error: unknown): boolean {
  const err = error as { code?: string };
  return err?.code === 'P2002';
}

/**
 * Extract unique constraint details from Prisma error
 */
export function extractUniqueConstraintDetails(error: unknown): {
  constraintFields: string[];
} | null {
  if (!isUniqueConstraintError(error)) {
    return null;
  }

  const err = error as { meta?: { target?: string[] } };
  const constraintFields = err.meta?.target || [];
  return { constraintFields };
}

// ─── CLIENT ADAPTER (Local/SQLite) ───

export abstract class ClientDatabaseAdapter<
  SqliteRowType extends Record<string, unknown | undefined>,
  CommonItemType extends CommonItem & { id: string; updatedAt: Date }
> {
  protected sqliteModel: BaseModel<CommonItemType, SqliteRowType>;
  protected tableName: string;

  constructor(sqliteModel: BaseModel<CommonItemType, SqliteRowType>, tableName: string) {
    this.sqliteModel = sqliteModel;
    this.tableName = tableName;
  }

  // ─── PUBLIC METHODS ───

  /**
   * Sync data between SQLite and common item format
   *
   * PROGRESS TRACKING:
   * The onProgress callback provides real-time updates during sync operations:
   * - operation: Human-readable description of current operation (e.g., "Syncing to cloud...")
   * - itemsComplete: Number of items processed so far
   * - totalItems: Total number of items to process
   *
   * @param direction - Direction of synchronization
   * @param onProgress - Optional progress callback function for real-time updates
   */
  public async sync(
    direction: SyncDirection = 'bidirectional',
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    try {
      switch (direction) {
        case 'toSqlite':
          await this.syncToSqlite(onProgress);
          break;
        case 'toItem':
          await this.syncToItem(onProgress);
          break;
        case 'bidirectional':
          await this.syncBidirectional(onProgress);
          break;
        default:
          throw new Error(`Invalid sync direction: ${direction}`);
      }
    } catch (error) {
      console.warn(`Error syncing ${this.tableName}:`, error);
      throw error;
    }
  }

  /**
   * Sync a specific item by ID between SQLite and common item format
   * @param id - The ID of the item to sync
   * @param direction - Direction of synchronization
   */
  public async syncById(
    id: string,
    direction: 'toSqlite' | 'toItem' | 'toPostgres' | 'bidirectional' = 'bidirectional'
  ): Promise<void> {
    try {
      switch (direction) {
        case 'toSqlite':
          await this.syncItemToSqlite(id);
          break;
        case 'toItem':
          await this.syncItemToItem(id);
          break;
        case 'toPostgres':
          await this.syncItemToPostgres(id);
          break;
        case 'bidirectional':
          await this.syncItemBidirectional(id);
          break;
        default:
          throw new Error(`Invalid sync direction: ${direction}`);
      }
    } catch (error) {
      console.warn(`Error syncing ${this.tableName} item ${id}:`, error);
      throw error;
    }
  }

  /**
   * Sync specific items by ID between SQLite and common item format
   * This method is optimized for rapid incremental updates of frequently changing objects.
   * It uses minimal field fetching for performance and only syncs the specified items.
   *
   * @param items - Record mapping item types to arrays of item IDs to sync
   * @param direction - Direction of synchronization
   * @param onProgress - Optional progress callback function for real-time updates
   */
  public async syncItems(
    items: SyncItemsInput,
    direction: 'toSqlite' | 'toItem' | 'toPostgres' | 'bidirectional' = 'bidirectional',
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    try {
      // Get the item IDs for this table type
      const itemsWithIds = items[this.tableName];
      if (!itemsWithIds || itemsWithIds.length === 0) {
        // This table type is not in the items to sync or has no IDs
        return;
      }

      const totalItems = itemsWithIds.length;

      if (onProgress) {
        onProgress(`Starting ${this.tableName} sync...`, 0, totalItems);
      }

      // For directions that send items to the server, use batch processing
      if (direction === 'toItem' || direction === 'bidirectional') {
        // Get the actual items from SQLite for the specified IDs
        const itemsToSync: CommonItemType[] = [];
        const errors: Array<{ itemId: string; error: unknown }> = [];

        for (const item of itemsWithIds) {
          const itemId = item.id;
          try {
            const sqliteItem = await this.getFromSqliteById(itemId);
            if (sqliteItem) {
              const commonItem = this.sqliteRowToItemWithNullConversion(sqliteItem);
              itemsToSync.push(commonItem);
            }
          } catch (error) {
            console.warn(`Error getting ${this.tableName} item ${itemId} from SQLite:`, error);
            errors.push({ itemId, error });
          }
        }

        if (itemsToSync.length > 0) {
          // Use batch processing with controlled concurrency
          await this.sendCommonItemsWithProgress(itemsToSync, (_operation, completed, _total) => {
            if (onProgress) {
              onProgress(`Syncing ${this.tableName}...`, completed, totalItems);
            }
          });
        }

        if (errors.length > 0) {
          console.warn(
            `${this.tableName} sync completed with ${errors.length} errors getting items from SQLite.`
          );
        }
      } else if (direction === 'toPostgres') {
        // Client adapters cannot sync directly to PostgreSQL
        throw new Error(
          `Client adapter for ${this.tableName} cannot sync directly to PostgreSQL. ` +
            `Use 'toItem' direction to sync to the cloud server, or use a ServerDatabaseAdapter for direct PostgreSQL access.`
        );
      } else {
        // 'toSqlite': batch fetch from server and upsert locally with progress
        const ids = itemsWithIds.map((x) => x.id);
        const batchedGetter: any = (this as any).getCommonItemsByIds;
        const fetchedItems: CommonItemType[] = batchedGetter
          ? await batchedGetter.call(this, ids)
          : ((
              (await Promise.all(
                ids.map((id) => this.getCommonItemById(id))
              )) as (CommonItemType | null)[]
            ).filter(Boolean) as CommonItemType[]);

        const sqliteRows = fetchedItems.map((it) => this.itemToSqliteRowWithNullConversion(it));
        await this.upsertListToSqliteWithProgress(sqliteRows, (_operation, completed, _total) => {
          if (onProgress) {
            onProgress(`Syncing ${this.tableName}...`, completed, totalItems);
          }
        });
      }

      if (onProgress) {
        onProgress(`Completed ${this.tableName} sync`, totalItems, totalItems);
      }
    } catch (error) {
      console.warn(`Error syncing ${this.tableName} items:`, error);
      throw error;
    }
  }

  /**
   * Verify sync integrity by comparing SQLite and REST data
   *
   * This method:
   * 1. Gets minimal items from both SQLite and REST
   * 2. Creates a map of id -> max(updatedAt)
   * 3. For items updated since the specified date, fetches full versions
   * 4. Uses ValidationUtils to compare the full items
   * 5. Logs any differences found
   *
   * @param sinceDate - Optional date to check items updated since this time (defaults to 5 minutes ago)
   */
  public async verifySync(sinceDate?: Date, diagnostic: boolean = false): Promise<void> {
    try {
      console.debug(`[${this.tableName}] Starting sync verification...`);

      // Get minimal items from both sources
      const sqliteMinimalItems = await this.getMinimalFromSqlite();
      const restMinimalItems = await this.getCommonItems(true);

      // Create maps for easy lookup
      const sqliteMap = new Map<string, MinimalItem>(
        sqliteMinimalItems.map((row) => [
          row.id as string,
          this.minimalSqliteRowToItem(
            row as Pick<SqliteRowType, 'id' | 'updatedAt'> & Record<string, unknown>
          )
        ])
      );
      const restMap = new Map<string, MinimalItem>(restMinimalItems.map((item) => [item.id, item]));

      // Create combined map of id -> max(updatedAt)
      const combinedMap = new Map<string, Date>();

      // Add SQLite items
      for (const [id, item] of sqliteMap) {
        const existing = combinedMap.get(id);
        if (!existing || item.updatedAt.getTime() > existing.getTime()) {
          combinedMap.set(id, item.updatedAt);
        }
      }

      // Add REST items
      for (const [id, item] of restMap) {
        const existing = combinedMap.get(id);
        if (!existing || item.updatedAt.getTime() > existing.getTime()) {
          combinedMap.set(id, item.updatedAt);
        }
      }

      // Find items updated since the specified date (defaults to 5 minutes ago)
      const cutoffDate = sinceDate || new Date(Date.now() - 5 * 60 * 1000);
      const recentItems: string[] = [];
      for (const [id, updatedAt] of combinedMap.entries()) {
        if (updatedAt > cutoffDate) {
          recentItems.push(id);
        }
      }

      console.debug(
        `[${this.tableName}] Found ${recentItems.length} items updated since ${cutoffDate.toISOString()}`
      );

      // Checksum-first verification: request remote checksums, compare to local, repair differences
      const adapterCfg: any = (this as any).getAdapterConfig
        ? (this as any).getAdapterConfig()
        : undefined;
      const authGetter: any = (this as any).getAuthTokenWithFallback;
      const auth = authGetter ? authGetter.call(this) : undefined;

      const restChecksums: Array<{ id: string; checksum: string | null }> = [];
      if (auth && recentItems.length > 0) {
        try {
          const { getAdapterChecksums } = await import('@repo/sharedlib/src/data/adapterChecksums');
          const maxConcurrent = adapterCfg?.maxConcurrent ?? 2;
          const batchSize = adapterCfg?.batchSize ?? 50;
          const delayBetweenBatches = adapterCfg?.delayBetweenBatches ?? 0;

          await processBatchesWithConcurrency(
            recentItems,
            async (idBatch) => {
              const part = await getAdapterChecksums(this.tableName, idBatch, auth, adapterCfg);
              restChecksums.push(...part);
            },
            { maxConcurrent, batchSize, delayBetweenBatches }
          );
        } catch (e) {
          console.debug(`[${this.tableName}] Failed to fetch remote checksums.`, e);
        }
      }

      const restChecksumMap = new Map(restChecksums.map((c) => [c.id, c.checksum ?? undefined]));
      const itemsToRepairToRest: CommonItemType[] = [];
      const differingIdsForDiagnostics: string[] = [];

      for (const itemId of recentItems) {
        try {
          const sqliteItem = await this.getFromSqliteById(itemId);
          if (!sqliteItem) continue;
          const sqliteCommonItem = this.sqliteRowToItemWithNullConversion(sqliteItem);
          const localChecksum = ValidationUtils.computeCommonChecksum(sqliteCommonItem);
          const remoteChecksum = restChecksumMap.get(itemId);
          const checksumEqual = remoteChecksum != null && remoteChecksum === localChecksum;
          const emoji = checksumEqual ? '✅' : '❌';
          console.debug(
            `[${this.tableName}] ${emoji} verifySync checksum id=${itemId}: checksumEqual=${checksumEqual} local=${localChecksum} remote=${remoteChecksum}`
          );
          if (!checksumEqual) {
            itemsToRepairToRest.push(sqliteCommonItem);
            differingIdsForDiagnostics.push(itemId);

            // Immediate diagnostic deep-compare for this item (ensures logs even if batch diag is skipped)
            if (diagnostic) {
              try {
                const restItemImmediate = await this.getCommonItemById(itemId, false);
                if (restItemImmediate) {
                  const validationImmediate = this.validateItemEquality(
                    sqliteCommonItem,
                    restItemImmediate
                  );
                  console.debug(
                    `[${this.tableName}] Immediate diagnostic deep compare id=${itemId}: isEqual=${validationImmediate.isEqual}`
                  );
                  // For transcripts, run specialized validator with logging enabled to get field-level diffs
                  try {
                    if ((this as any).tableName === 'transcript') {
                      ValidationUtils.validateTranscriptEquality(
                        sqliteCommonItem as any,
                        restItemImmediate as any,
                        true
                      );
                    }
                  } catch (e) {
                    console.debug(
                      `[${this.tableName}] Immediate transcript validator logging error for ${itemId}:`,
                      e
                    );
                  }
                  // Log checksum inputs to help explain mismatches
                  try {
                    const restChecksumImmediate =
                      ValidationUtils.computeCommonChecksum(restItemImmediate);
                    console.debug(
                      `[${this.tableName}] Immediate diagnostic checksums id=${itemId}: local=${localChecksum} remote=${restChecksumImmediate}`
                    );
                  } catch (e) {
                    console.debug(
                      `[${this.tableName}] Immediate diagnostic checksum compute error for ${itemId}:`,
                      e
                    );
                  }
                  // Always log normalized payload differences when checksum mismatches
                  try {
                    const normLocal = ValidationUtils.toUniformComparable(
                      ValidationUtils.dropNullishKeysDeep(sqliteCommonItem)
                    );
                    const normRemote = ValidationUtils.toUniformComparable(
                      ValidationUtils.dropNullishKeysDeep(restItemImmediate)
                    );
                    const sLocal = JSON.stringify(normLocal);
                    const sRemote = JSON.stringify(normRemote);
                    const same = sLocal === sRemote;
                    console.debug(
                      `[${this.tableName}] Immediate diagnostic normalized compare id=${itemId}: equal=${same} ` +
                        `(localLen=${sLocal.length}, remoteLen=${sRemote.length})`
                    );

                    if (!same) {
                      // Log the actual JSON difference for debugging
                      console.debug(
                        'Types: ',
                        typeof sqliteCommonItem,
                        typeof restItemImmediate,
                        typeof sqliteCommonItem.duration,
                        typeof restItemImmediate.duration
                      );
                      console.debug(
                        'Values: ',
                        sqliteCommonItem,
                        restItemImmediate,
                        sqliteCommonItem.duration,
                        restItemImmediate.duration
                      );
                      console.log(
                        `[${this.tableName}] Immediate diagnostic JSON diff ${itemId}:\n` +
                          `LOCAL:  ${sLocal.slice(0, 2000)}\n` +
                          `REMOTE: ${sRemote.slice(0, 2000)}`
                      );
                      // Produce a concise field-level diff for top-level keys
                      const keys = Array.from(
                        new Set([...Object.keys(normLocal || {}), ...Object.keys(normRemote || {})])
                      );
                      for (const k of keys) {
                        const a = (normLocal as any)?.[k];
                        const b = (normRemote as any)?.[k];
                        const eq = JSON.stringify(a) === JSON.stringify(b);
                        if (!eq) {
                          console.log(
                            `[${this.tableName}] Immediate diagnostic field diff ${itemId}.${k}: ` +
                              `local=${JSON.stringify(a)?.slice(0, 500)} remote=${JSON.stringify(b)?.slice(0, 500)}`
                          );
                        }
                      }
                    }
                  } catch (e) {
                    console.debug(
                      `[${this.tableName}] Immediate diagnostic normalized compare error for ${itemId}:`,
                      e
                    );
                  }
                  if (!validationImmediate.isEqual) {
                    for (const difference of validationImmediate.differences) {
                      console.log(
                        `[${this.tableName}] Immediate diagnostic diff ${itemId}: ${difference.slice(0, 1000)}`
                      );
                    }
                  }
                } else {
                  console.debug(
                    `[${this.tableName}] Immediate diagnostic: REST item not found for id=${itemId}`
                  );
                }
              } catch (e) {
                console.debug(
                  `[${this.tableName}] Immediate diagnostic compare error for ${itemId}:`,
                  e
                );
              }
            }
          }
        } catch (error) {
          console.debug(`[${this.tableName}] Error verifying item ${itemId}:`, error);
        }
      }

      // Batch repair to REST for differing items
      if (itemsToRepairToRest.length > 0) {
        console.info(
          `[${this.tableName}] Repairing ${itemsToRepairToRest.length} items to REST based on checksum diffs`
        );
        try {
          await this.sendCommonItemsWithProgress(itemsToRepairToRest);
        } catch (repairErr) {
          console.warn(`[${this.tableName}] Repair upsert batch failed:`, repairErr);
        }
      }

      // Optional diagnostic: fetch REST items and deep-compare
      if (diagnostic && differingIdsForDiagnostics.length > 0) {
        const batchedGetterDiag: any = (this as any).getCommonItemsByIds;
        const restItemsDiag: CommonItemType[] = batchedGetterDiag
          ? await batchedGetterDiag.call(this, differingIdsForDiagnostics, false)
          : ((
              (await Promise.all(
                differingIdsForDiagnostics.map((id) => this.getCommonItemById(id, false))
              )) as (CommonItemType | null)[]
            ).filter(Boolean) as CommonItemType[]);
        const restByIdDiag = new Map<string, CommonItemType>(restItemsDiag.map((i) => [i.id, i]));
        for (const itemId of differingIdsForDiagnostics) {
          try {
            const sqliteItem = await this.getFromSqliteById(itemId);
            if (!sqliteItem) continue;
            const sqliteCommonItem = this.sqliteRowToItemWithNullConversion(sqliteItem);
            const restItem = restByIdDiag.get(itemId);
            if (!restItem) continue;
            const validation = this.validateItemEquality(sqliteCommonItem, restItem);
            console.debug(
              `[${this.tableName}] Diagnostic deep compare id=${itemId}: isEqual=${validation.isEqual}`
            );
            // For transcripts, run specialized validator with logging enabled to get field-level diffs
            try {
              if ((this as any).tableName === 'transcript') {
                ValidationUtils.validateTranscriptEquality(
                  sqliteCommonItem as any,
                  restItem as any,
                  true
                );
              }
            } catch (e) {
              console.debug(
                `[${this.tableName}] Diagnostic transcript validator logging error for ${itemId}:`,
                e
              );
            }
            if (!validation.isEqual) {
              for (const difference of validation.differences) {
                console.log(
                  `[${this.tableName}] Diagnostic diff ${itemId}: ${difference.slice(0, 1000)}`
                );
              }
            }
          } catch (e) {
            console.debug(`[${this.tableName}] Diagnostic compare error for ${itemId}:`, e);
          }
        }
      }

      console.debug(`[${this.tableName}] Sync verification complete`);
    } catch (error) {
      console.debug(`[${this.tableName}] Error during sync verification:`, error);
    }
  }

  /**
   * Deep equality comparison that ignores property order
   */
  protected deepEqual(obj1: any, obj2: any): boolean {
    // Handle primitive types and null/undefined
    if (obj1 === obj2) return true;
    // Treat null and undefined as equivalent for database compatibility
    if (obj1 == null && obj2 == null) return true;
    if (obj1 == null || obj2 == null) return false;

    // Handle Date objects - compare Date-to-Date or Date-to-string
    if (obj1 instanceof Date && obj2 instanceof Date) {
      return obj1.getTime() === obj2.getTime();
    }

    // Handle Date-to-string comparison (for JSON round-trip)
    if (obj1 instanceof Date && typeof obj2 === 'string') {
      return obj1.toISOString() === obj2;
    }

    if (obj2 instanceof Date && typeof obj1 === 'string') {
      return obj2.toISOString() === obj1;
    }

    // If only one is a Date and the other is not a string, they're not equal
    if (obj1 instanceof Date || obj2 instanceof Date) {
      return false;
    }

    // Handle URL objects - compare URL-to-URL or URL-to-string
    if (obj1 instanceof URL && obj2 instanceof URL) {
      return obj1.toString() === obj2.toString();
    }

    // Handle URL-to-string comparison (for JSON round-trip)
    if (obj1 instanceof URL && typeof obj2 === 'string') {
      return obj1.toString() === obj2;
    }

    if (obj2 instanceof URL && typeof obj1 === 'string') {
      return obj2.toString() === obj1;
    }

    // If only one is a URL and the other is not a string, they're not equal
    if (obj1 instanceof URL || obj2 instanceof URL) {
      return false;
    }

    if (typeof obj1 !== typeof obj2) return false;

    // Handle arrays
    if (Array.isArray(obj1)) {
      if (!Array.isArray(obj2) || obj1.length !== obj2.length) return false;
      return obj1.every((item, index) => this.deepEqual(item, obj2[index]));
    }

    // Handle objects
    if (typeof obj1 === 'object') {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      return keys1.every((key) => keys2.includes(key) && this.deepEqual(obj1[key], obj2[key]));
    }

    return false;
  }

  /**
   * Validate equality between two items using a simple field-by-field comparison
   * This is a simplified version of ValidationUtils.validateTranscriptEquality
   */
  protected validateItemEquality(
    item1: CommonItemType,
    item2: CommonItemType
  ): { isEqual: boolean; differences: string[] } {
    const differences: string[] = [];

    // Get all keys from both objects
    const allKeys = new Set([...Object.keys(item1), ...Object.keys(item2)]);

    for (const key of allKeys) {
      const val1 = (item1 as any)[key];
      const val2 = (item2 as any)[key];

      if (key === 'createdAt' || key === 'updatedAt' || key === 'deletedAt') {
        // Handle date comparison with 1 second tolerance
        if (val1 && val2) {
          const time1 = val1 instanceof Date ? val1.getTime() : new Date(val1).getTime();
          const time2 = val2 instanceof Date ? val2.getTime() : new Date(val2).getTime();

          if (Math.abs(time1 - time2) > 1000) {
            differences.push(
              `Date field '${key}' differs by more than 1 second: ${val1} vs ${val2}`
            );
          }
        } else if (val1 !== val2) {
          differences.push(`Date field '${key}' differs: ${val1} vs ${val2}`);
        }
      } else if (typeof val1 === 'object' && typeof val2 === 'object') {
        // Handle object comparison (JSON fields) using deep equality
        if (!this.deepEqual(val1, val2)) {
          // Add debug logging for meeting field specifically
          if (key === 'meeting') {
            console.log(`[DEBUG] Base adapter meeting field comparison failed:`);
            console.log(`  Val1:`, JSON.stringify(val1));
            console.log(`  Val2:`, JSON.stringify(val2));
            console.log(`  Val1 type:`, typeof val1);
            console.log(`  Val2 type:`, typeof val2);
            console.log(`  Deep equal result:`, this.deepEqual(val1, val2));
          }

          differences.push(
            `JSON field '${key}' differs: ${JSON.stringify(val1)} vs ${JSON.stringify(val2)}`
          );
        }
      } else {
        // Handle simple value comparison
        if (val1 !== val2) {
          differences.push(`Field '${key}' differs: ${val1} vs ${val2}`);
        }
      }
    }

    return {
      isEqual: differences.length === 0,
      differences
    };
  }

  // ─── PROTECTED ABSTRACT METHODS (MUST BE IMPLEMENTED BY SUBCLASSES) ───

  /**
   * Convert SQLite row to common item format
   * @param row - SQLite row data
   * @returns Common item format
   */
  protected abstract sqliteRowToItem(row: SqliteRowType): CommonItemType;

  /**
   * Convert SQLite row to common item format with null-to-undefined conversion
   * This is the public method that ensures null values are converted to undefined
   * @param row - SQLite row data
   * @returns Common item format with null values converted to undefined
   */
  protected sqliteRowToItemWithNullConversion(row: SqliteRowType): CommonItemType {
    const item = this.sqliteRowToItem(row);
    return this.sanitizeCommonItem(convertNullToUndefined(item));
  }

  /**
   * Convert minimal SQLite row to minimal common item format
   * @param row - Minimal SQLite row data
   * @returns Minimal common item format
   */
  protected abstract minimalSqliteRowToItem(
    row: Pick<SqliteRowType, 'id' | 'updatedAt'> & Record<string, unknown>
  ): MinimalItem;

  /**
   * Convert common item to SQLite row format
   * @param item - Common item data
   * @returns SQLite row format
   */
  protected abstract itemToSqliteRow(item: CommonItemType): SqliteRowType;

  /**
   * Validate that an item has all required fields before saving to SQLite
   * This prevents minimal or incomplete items from being saved
   * @param item - Common item to validate
   */
  protected validateItemForSqlite(item: CommonItemType): void {
    if (!item.id) {
      throw new Error('Item must have an id');
    }
    if (!(item as any).updatedAt) {
      throw new Error(`Item ${item.id} must have an updatedAt`);
    }
    // Additional validation can be added by subclasses
  }

  /**
   * Validate that a SQLite row has all required fields before saving
   * This prevents minimal or incomplete rows from being saved
   * @param row - SQLite row to validate
   */
  protected validateSqliteRow(row: SqliteRowType): void {
    if (!row.id) {
      throw new Error('SQLite row must have an id');
    }
    if (!(row as any).updatedAt) {
      throw new Error(`SQLite row ${row.id} must have an updatedAt`);
    }
    // Additional validation can be added by subclasses
  }

  /**
   * Validate that an item has all required fields before sending to cloud
   * This prevents minimal or incomplete items from being sent
   * @param item - Common item to validate
   */
  protected validateItemForCloud(item: CommonItemType): void {
    if (!item.id) {
      throw new Error('Item must have an id');
    }
    if (!(item as any).updatedAt) {
      throw new Error(`Item ${item.id} must have an updatedAt`);
    }
    // Additional validation can be added by subclasses
  }

  /**
   * Convert common item to SQLite row format with null-to-undefined conversion
   * This ensures that any null values in the input item are converted to undefined
   * @param item - Common item data
   * @returns SQLite row format with null values converted to undefined
   */
  protected itemToSqliteRowWithNullConversion(item: CommonItemType): SqliteRowType {
    const convertedItem = convertNullToUndefined(item);
    return this.itemToSqliteRow(convertedItem);
  }

  /**
   * Get all items from SQLite
   * @returns Array of SQLite rows
   */
  protected abstract getAllFromSqlite(): Promise<SqliteRowType[]>;

  /**
   * Get minimal items from SQLite for performance optimization
   * Should return only essential fields: id, updatedAt, and fields needed for deduplication
   * @returns Array of minimal SQLite rows
   */
  protected abstract getMinimalFromSqlite(): Promise<
    Array<Pick<SqliteRowType, 'id' | 'updatedAt'> & Record<string, unknown>>
  >;

  /**
   * Get item by ID from SQLite
   * @param id - Item ID
   * @returns SQLite row or null
   */
  protected abstract getFromSqliteById(id: string): Promise<SqliteRowType | null>;

  /**
   * Create or update item in SQLite
   * @param row - SQLite row data
   */
  protected async upsertToSqlite(row: SqliteRowType): Promise<void> {
    await this.upsertListToSqlite([row]);
  }

  /**
   * Create or update items in SQLite
   * This method delegates to the specific adapter implementation
   * @param rows - SQLite row data
   */
  protected async upsertListToSqlite(rows: SqliteRowType[]): Promise<void> {
    // Early return for empty arrays
    if (rows.length === 0) {
      return;
    }

    // Delegate to the specific adapter implementation
    await this.upsertListToSqliteImpl(rows);
  }

  /**
   * Abstract method for specific adapter implementations to handle bulk upserts
   * This allows each adapter to implement its own optimized upsert strategy
   * @param rows - SQLite row data
   */
  protected abstract upsertListToSqliteImpl(rows: SqliteRowType[]): Promise<void>;

  public async upsertCommonListToSqlite(rows: CommonItemType[]): Promise<void> {
    // Early return for empty arrays to avoid ORM issues
    if (rows.length === 0) {
      return;
    }

    // Validate that all items are complete before saving to SQLite
    for (const row of rows) {
      this.validateItemForSqlite(row);
    }

    const sqliteRows = rows.map((row) => this.itemToSqliteRowWithNullConversion(row));
    await this.upsertListToSqlite(sqliteRows);
  }

  /**
   * Get common items from external source (REST layer - not used in server context)
   * @param minimal - Whether to return minimal data only
   * @returns Array of common items
   */
  protected async getCommonItems(_minimal?: boolean): Promise<CommonItemType[]> {
    throw new Error(
      `Server adapter for ${this.tableName} cannot fetch items from client via REST - this would be firewalled`
    );
  }

  /**
   * Get common item by ID from external source (REST layer - not used in server context)
   * @param id - Item ID
   * @param minimal - Whether to return minimal data only
   * @returns Common item or null
   */
  protected async getCommonItemById(
    id: string,
    _minimal?: boolean
  ): Promise<CommonItemType | null> {
    throw new Error(
      `Server adapter for ${this.tableName} cannot fetch item ${id} from client via REST - this would be firewalled`
    );
  }

  /**
   * Send common item to external source (to be implemented by REST layer)
   * @param item - Common item to send
   */
  protected abstract sendCommonItem(item: CommonItemType): Promise<void>;

  protected async sendCommonItems(items: CommonItemType[]): Promise<void> {
    // Default implementation: process items individually
    // This will be overridden by PrivateClientDatabaseAdapter to use proper batch processing
    await Promise.all(
      items.map(async (item) => {
        await this.sendCommonItem(item);
      })
    );
  }

  /**
   * Send common items to external source with progress tracking (parallelized)
   *
   * This override parallelizes network requests for faster sync.
   * Calls onProgress as each item completes (order not guaranteed).
   *
   * @param items - Array of common items to send
   * @param onProgress - Progress callback function
   */
  protected async sendCommonItemsWithProgress(
    items: CommonItemType[],
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    // Default implementation: use sendCommonItems for batch processing
    // This will be overridden by PrivateClientDatabaseAdapter to use proper batch processing
    await this.sendCommonItems(items);

    if (onProgress) {
      onProgress(`Syncing ${this.tableName} to cloud...`, items.length, items.length);
    }
  }

  /**
   * Batch upsert to SQLite with progress tracking
   *
   * PROGRESS TRACKING:
   * Sequential processing with per-item progress updates:
   * - Calls onProgress('Syncing to local database...', i + 1, rows.length) after each item
   * - Default implementation processes items sequentially for better progress granularity
   * - Override in subclasses for parallel processing if needed
   *
   * @param rows - Array of SQLite rows to upsert
   * @param onProgress - Progress callback function
   */
  protected async upsertListToSqliteWithProgress(
    rows: SqliteRowType[],
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    // Default implementation: sequential upserts with progress
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      // Validate that the row has required fields before saving to SQLite
      this.validateSqliteRow(row);

      await this.upsertToSqlite(row);
      if (onProgress) {
        onProgress(`Syncing ${this.tableName} to local database...`, i + 1, rows.length);
      }
    }
  }

  /**
   * Determine which item is newer based on updatedAt timestamp
   * @param sqliteItem - SQLite item
   * @param commonItem - Common item
   * @returns 'sqlite' | 'common' | 'equal'
   */
  protected abstract getNewerItem(
    sqliteItem: CommonItemType,
    commonItem: CommonItemType
  ): 'sqlite' | 'common' | 'equal';

  /**
   * Get deduplication strategy for this table
   * @returns Deduplication configuration
   */
  protected abstract getDeduplicationStrategy(): DeduplicationStrategy<CommonItemType>;

  /**
   * Get deduplication key for an item
   * @param item - Common item
   * @returns Deduplication key
   */
  protected getDeduplicationKey(item: CommonItemType): string {
    const strategy = this.getDeduplicationStrategy();
    if (strategy.type === 'none') {
      return item.id;
    }
    return strategy.getKey(item);
  }

  /**
   * Sanitize a common item by removing undefined-valued keys deeply.
   * This ensures we don't rely on undefined keys being serialized.
   */
  protected sanitizeCommonItem<T extends Record<string, any>>(item: T): T {
    const prune = (val: any): any => {
      if (val === undefined) return undefined;
      if (val === null) return null;
      // Preserve special objects
      if (val instanceof Date) return val;
      if (val instanceof URL) return val;
      if (Array.isArray(val)) {
        const arr = val.map(prune);
        return arr;
      }

      if (typeof val === 'object' && val !== null) {
        const out: Record<string, any> = {};
        for (const [k, v] of Object.entries(val)) {
          const pv = prune(v);
          if (pv !== undefined) {
            out[k] = pv;
          }
        }
        return out as any;
      }
      return val;
    };

    return prune(item);
  }

  /**
   * Fields to exclude when computing checksums (override per table if needed)
   */
  protected getChecksumExcludeFields(): string[] {
    return [];
  }

  /**
   * Group items by their deduplication key
   * @param items - Array of items to group
   * @returns Map of deduplication key to array of items
   */
  protected groupItemsByDeduplicationKey(items: CommonItemType[]): Map<string, CommonItemType[]> {
    const groups = new Map<string, CommonItemType[]>();
    for (const item of items) {
      const key = this.getDeduplicationKey(item);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    }
    return groups;
  }

  /**
   * Get the latest item from a group based on updatedAt timestamp
   * @param items - Array of items to compare
   * @returns The item with the latest updatedAt timestamp
   */
  protected getLatestItem(items: CommonItemType[]): CommonItemType {
    if (items.length === 0) {
      throw new Error('Cannot get latest item from empty array');
    }
    return items.reduce((latest, current) => {
      return current.updatedAt.getTime() > latest.updatedAt.getTime() ? current : latest;
    });
  }

  /**
   * Deduplicate items by keeping only the latest item per deduplication key
   * @param items - Array of items to deduplicate
   * @returns Array of deduplicated items
   */
  protected deduplicateItems(items: CommonItemType[]): CommonItemType[] {
    const groups = this.groupItemsByDeduplicationKey(items);
    const deduplicated: CommonItemType[] = [];

    for (const [_key, groupItems] of groups) {
      if (groupItems.length > 0) {
        if (groupItems.length > 1) {
          // Log the items being deduplicated with more context
          groupItems.forEach((_item, _index) => {
            // const source = item.createdAt?.getTime() === 0 ? 'SQLite' : 'REST API';
          });
        }

        const latestItem = this.getLatestItem(groupItems);
        deduplicated.push(latestItem);
      }
    }

    return deduplicated;
  }

  /**
   * Optimized method to get a single minimal item from SQLite by id
   */
  protected async getMinimalItemFromSqlite(id: string): Promise<MinimalItem | null> {
    const sqliteItem = await this.getFromSqliteById(id);
    if (sqliteItem) {
      const minimalItem = this.minimalSqliteRowToItem(sqliteItem as any);
      return {
        id: minimalItem.id,
        updatedAt: minimalItem.updatedAt,
        createdAt: minimalItem.createdAt
      };
    }
    return null;
  }

  // ─── PROTECTED CONCRETE METHODS ───

  /**
   * Sync all data from common items to SQLite
   *
   * PROGRESS TRACKING:
   * Calls onProgress for each item processed:
   * - Initial call: (0, totalItems) when starting
   * - Per item: (i + 1, totalItems) after each item is upserted
   *
   * @param onProgress - Optional callback for progress updates
   */
  protected async syncToSqlite(
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    const commonItems = await this.getCommonItems();
    const totalItems = commonItems.length;

    if (onProgress) {
      onProgress(`Syncing ${this.tableName} items to local database...`, 0, totalItems);
    }

    for (let i = 0; i < commonItems.length; i++) {
      const item = commonItems[i];
      const sqliteRow = this.itemToSqliteRowWithNullConversion(item);
      await this.upsertToSqlite(sqliteRow);
      if (onProgress) {
        onProgress(`Syncing ${this.tableName} items to local database...`, i + 1, totalItems);
      }
    }
  }

  /**
   * Sync all data from SQLite to common items
   *
   * PROGRESS TRACKING:
   * Calls onProgress for each item processed, including items with foreign key errors:
   * - Initial call: (0, totalItems) when starting
   * - Per item: (i + 1, totalItems) after each item is sent (success or error)
   *
   * Note: Foreign key constraint errors are logged but don't stop progress tracking
   *
   * @param onProgress - Optional callback for progress updates
   */
  protected async syncToItem(
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    const sqliteItems = await this.getAllFromSqlite();
    const commonItems = sqliteItems.map((row) => this.sqliteRowToItemWithNullConversion(row));
    const totalItems = commonItems.length;

    if (onProgress) {
      onProgress(`Syncing ${this.tableName} items to cloud...`, 0, totalItems);
    }

    if (commonItems.length > 0) {
      // Use batch processing with controlled concurrency
      await this.sendCommonItemsWithProgress(commonItems, (_operation, completed, _total) => {
        if (onProgress) {
          onProgress(`Syncing ${this.tableName} items to cloud...`, completed, totalItems);
        }
      });
    }

    if (onProgress) {
      onProgress(`Syncing ${this.tableName} items to cloud...`, totalItems, totalItems);
    }
  }

  /**
   * Bidirectional sync with conflict resolution and deduplication
   *
   * PROGRESS TRACKING:
   * This is the most complex sync operation with multiple phases:
   *
   * 1. ANALYSIS PHASE:
   *    - onProgress('Analyzing data...', 0, 0) - Initial analysis
   *
   * 2. CLOUD SYNC PHASE:
   *    - onProgress('Syncing to cloud...', completedOperations, totalOperations)
   *    - Uses sendCommonItemsWithProgress for granular updates
   *
   * 3. LOCAL SYNC PHASE:
   *    - onProgress('Syncing to local database...', completedOperations, totalOperations)
   *    - Uses upsertListToSqliteWithProgress for granular updates
   *
   * @param onProgress - Optional callback for progress updates
   */
  protected async syncBidirectional(
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    if (onProgress) {
      // Use (0, 0) to indicate analysis phase where we don't yet know total items
      // This allows the UI to show meaningful progress even during initial analysis
      // The aggregate progress system will handle this specially and not advance progress
      let userDisplayedName = this.tableName !== 'transcript' ? this.tableName : 'knowledge base';
      onProgress(`Verifying ${userDisplayedName} data...`, 0, 0);
    }

    // STAGE 1: Get minimal items for analysis (performance optimization)
    const sqliteMinimalItems = await this.getMinimalFromSqlite();
    const commonMinimalItems = await this.getCommonItems(true);

    for (const item of commonMinimalItems) {
      if (typeof item.id !== 'string' || item.id.length > 100) {
        console.warn(`Item ID is required, got:`, item.id);
        throw new Error(`Item ID is required`);
      }
    }

    for (const item of sqliteMinimalItems) {
      if (typeof item.id !== 'string' || item.id.length > 100) {
        console.warn(`Item ID is required, got:`, item);
        throw new Error(`Item ID is required`);
      }
    }

    const sqliteMinimalMap = new Map<string, MinimalItem>(
      sqliteMinimalItems.map((row) => [
        String(row.id),
        this.minimalSqliteRowToItem(
          row as Pick<SqliteRowType, 'id' | 'updatedAt'> & Record<string, any>
        )
      ])
    );
    const commonMinimalMap = new Map(commonMinimalItems.map((item) => [item.id, item]));

    // Apply deduplication to the combined minimal items for non-PK unique key handling
    // Note: We need to convert minimal items to common items for deduplication
    const allMinimalItems = [
      ...Array.from(sqliteMinimalMap.values()).map(
        (minimal) => ({ ...minimal, createdAt: new Date(0) }) as CommonItemType
      ),
      ...commonMinimalItems
    ];

    const deduplicatedMinimalItems = this.deduplicateItems(allMinimalItems);

    const itemsToSendToCommon: CommonItemType[] = [];
    const itemsToUpsertToSqlite: SqliteRowType[] = [];
    let conflictsResolved = 0;
    let itemsOnlyInSqlite = 0;
    let itemsOnlyInCommon = 0;

    // STAGE 2: Compare deduplicated items and determine fetch lists (no fetching inside loop)
    const sqliteIdsToFetch: Array<string> = [];
    const commonIdsToFetch: Array<string> = [];

    for (const deduplicatedItem of deduplicatedMinimalItems) {
      const itemId = String(deduplicatedItem.id);
      if (typeof itemId !== 'string' || itemId.length > 100) {
        console.warn(`Item ID is required, got:`, itemId);
        throw new Error(`Item ID is required`);
      }
      const sqliteMinimalItem = sqliteMinimalMap.get(itemId);
      const commonMinimalItem = commonMinimalMap.get(itemId);

      if (sqliteMinimalItem && commonMinimalItem) {
        // Item exists in both - resolve conflicts
        conflictsResolved++;
        const newer = this.getNewerItem(
          { ...sqliteMinimalItem, createdAt: new Date(0) } as CommonItemType,
          commonMinimalItem
        );
        if (newer === 'sqlite') {
          sqliteIdsToFetch.push(itemId);
        } else if (newer === 'common') {
          commonIdsToFetch.push(itemId);
        }
        // If equal, no action needed
      } else if (sqliteMinimalItem) {
        // Only in SQLite - sync to common
        itemsOnlyInSqlite++;
        sqliteIdsToFetch.push(itemId);
      } else if (commonMinimalItem) {
        // Only in common - sync to SQLite
        itemsOnlyInCommon++;
        commonIdsToFetch.push(itemId);
      }
    }

    // Fetch batches from both sources using adapterConfig for concurrency/batch sizing
    const adapterConfig: Partial<AdapterConfig> | undefined = (this as any).getAdapterConfig
      ? (this as any).getAdapterConfig()
      : undefined;

    const maxConcurrent = adapterConfig?.maxConcurrent;
    const batchSize = adapterConfig?.batchSize;
    const delayBetweenBatches = adapterConfig?.delayBetweenBatches;

    // Helper to optionally delay between batches
    const maybeDelay = async () => {
      if (delayBetweenBatches && delayBetweenBatches > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayBetweenBatches));
      }
    };

    // Fetch complete SQLite items in batches
    if (sqliteIdsToFetch.length > 0) {
      await processBatchesWithConcurrency(
        sqliteIdsToFetch,
        async (batch) => {
          // Fetch this batch in parallel with a reasonable per-batch concurrency (local DB)
          const rows = await Promise.all(batch.map((id) => this.getFromSqliteById(id)));
          for (const row of rows) {
            if (!row) continue;
            const sqliteCommonItem = this.sqliteRowToItemWithNullConversion(row);
            itemsToSendToCommon.push(sqliteCommonItem);
          }
          await maybeDelay();
        },
        { maxConcurrent, batchSize }
      );
    }

    // Fetch complete Common items in batches (use REST batch endpoint via subclass override when available)
    if (commonIdsToFetch.length > 0) {
      await processBatchesWithConcurrency(
        commonIdsToFetch,
        async (batch) => {
          // Prefer a batched method if subclass provides one
          const batchedGetter: any = (this as any).getCommonItemsByIds;
          const items: CommonItemType[] = batchedGetter
            ? await batchedGetter.call(this, batch, false) // Explicitly request full items, not minimal
            : ((
                (await Promise.all(
                  batch.map((id) => this.getCommonItemById(id, false)) // Explicitly request full items, not minimal
                )) as (CommonItemType | null)[]
              ).filter(Boolean) as CommonItemType[]);
          for (const item of items) {
            const sqliteRow = this.itemToSqliteRowWithNullConversion(item as CommonItemType);
            itemsToUpsertToSqlite.push(sqliteRow);
          }
          await maybeDelay();
        },
        { maxConcurrent, batchSize }
      );
    }

    const totalOperations = itemsToSendToCommon.length + itemsToUpsertToSqlite.length;
    let completedOperations = 0;

    console.log(
      `[${this.tableName}] Starting bidirectional sync: ${deduplicatedMinimalItems.length} total deduplicated items`
    );
    console.log(
      `[${this.tableName}] Conflicts to resolve: ${conflictsResolved}, Only in SQLite: ${itemsOnlyInSqlite}, Only in Common: ${itemsOnlyInCommon}`
    );

    let syncedToCommon = 0;
    let syncedToSqlite = 0;
    let deferredToCommon = 0;
    let deferredToSqlite = 0;

    // Batch send to common with progress
    if (itemsToSendToCommon.length > 0) {
      if (onProgress) {
        onProgress('Syncing to cloud...', completedOperations, totalOperations);
      }
      try {
        await this.sendCommonItemsWithProgress(
          itemsToSendToCommon,
          (operation, complete, _total) => {
            if (onProgress) {
              onProgress(operation, completedOperations + complete, totalOperations);
            }
          }
        );
        syncedToCommon = itemsToSendToCommon.length;
        completedOperations += itemsToSendToCommon.length;
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          deferredToCommon = itemsToSendToCommon.length;
          completedOperations += itemsToSendToCommon.length;
          console.warn(
            `[${this.tableName}] All ${itemsToSendToCommon.length} items deferred to common due to foreign key constraint`
          );
        } else {
          console.error(`Error syncing ${this.tableName}:`, error);
          throw error;
        }
      }
    }

    // Batch upsert to SQLite with progress
    if (itemsToUpsertToSqlite.length > 0) {
      if (onProgress) {
        onProgress(
          `Syncing ${this.tableName} items to local database...`,
          completedOperations,
          totalOperations
        );
      }
      try {
        await this.upsertListToSqliteWithProgress(
          itemsToUpsertToSqlite,
          (operation, complete, _total) => {
            if (onProgress) {
              onProgress(operation, completedOperations + complete, totalOperations);
            }
          }
        );
        syncedToSqlite = itemsToUpsertToSqlite.length;
        completedOperations += itemsToUpsertToSqlite.length;
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          deferredToSqlite = itemsToUpsertToSqlite.length;
          completedOperations += itemsToUpsertToSqlite.length;
          console.warn(
            `[${this.tableName}] All ${itemsToUpsertToSqlite.length} items deferred to SQLite due to foreign key constraint`
          );
        } else {
          throw error;
        }
      }
    }

    console.log(
      `[${this.tableName}] Sync completed: ${syncedToCommon} synced to common, ${syncedToSqlite} synced to SQLite, ${deferredToCommon} deferred to common, ${deferredToSqlite} deferred to SQLite`
    );

    // Log sync status summary
    const totalSynced = syncedToCommon + syncedToSqlite;
    const totalDeferred = deferredToCommon + deferredToSqlite;
    const isFullySynced = totalSynced === 0 && totalDeferred === 0;

    if (isFullySynced) {
      console.log(`[${this.tableName}] ✅ Databases are in sync (no changes needed)`);
    } else if (totalDeferred > 0) {
      console.log(
        `[${this.tableName}] ⚠️  Sync partially complete (${totalDeferred} items deferred)`
      );
    } else {
      console.log(`[${this.tableName}] ✅ Sync complete (${totalSynced} items synced)`);
    }
  }

  /**
   * Sync specific item to SQLite
   */
  protected async syncItemToSqlite(id: string): Promise<void> {
    const commonItem = await this.getCommonItemById(id);
    if (commonItem) {
      const sqliteRow = this.itemToSqliteRowWithNullConversion(commonItem);
      await this.upsertToSqlite(sqliteRow);
    }
  }

  /**
   * Sync specific item to PostgreSQL
   */
  protected async syncItemToPostgres(id: string): Promise<void> {
    const sqliteItem = await this.getFromSqliteById(id);
    if (sqliteItem) {
      try {
        const commonItem = this.sqliteRowToItemWithNullConversion(sqliteItem);
        await this.sendCommonItem(commonItem);
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          console.warn(
            `Foreign key constraint error for ${this.tableName} item ${id}: ${error.message}. ` +
              `This item will be retried in a future sync when dependencies are available.`
          );
          // Don't re-throw foreign key constraint errors for individual items
        } else {
          // Re-throw non-foreign key constraint errors
          throw error;
        }
      }
    }
  }

  /**
   * Sync specific item to common
   */
  protected async syncItemToItem(id: string): Promise<void> {
    const sqliteItem = await this.getFromSqliteById(id);
    if (sqliteItem) {
      try {
        const commonItem = this.sqliteRowToItemWithNullConversion(sqliteItem);
        await this.sendCommonItem(commonItem);
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          console.warn(
            `Foreign key constraint error for ${this.tableName} item ${id}: ${error.message}. ` +
              `This item will be retried in a future sync when dependencies are available.`
          );
          // Don't re-throw foreign key constraint errors for individual items
        } else {
          // Re-throw non-foreign key constraint errors
          throw error;
        }
      }
    }
  }
  /**
   * Bidirectional sync for specific item with minimal item optimization
   */
  protected async syncItemBidirectional(id: string): Promise<void> {
    // STAGE 1: Get minimal items for comparison (performance optimization)
    const sqliteMinimalItem = await this.getMinimalItemFromSqlite(id);
    const commonMinimalItem = await this.getCommonItemById(id, true); // Get minimal version

    try {
      if (sqliteMinimalItem && commonMinimalItem) {
        // Item exists in both - resolve conflicts using minimal items
        const sqliteMinimal = this.minimalSqliteRowToItem(
          sqliteMinimalItem as unknown as Pick<SqliteRowType, 'id' | 'updatedAt'> &
            Record<string, any>
        );

        const newer = this.getNewerItem(
          { ...sqliteMinimal, createdAt: new Date(0) } as CommonItemType,
          commonMinimalItem
        );

        if (newer === 'sqlite') {
          // Fetch complete SQLite item and sync to common
          const completeSqliteItem = await this.getFromSqliteById(id);
          if (completeSqliteItem) {
            const sqliteCommonItem = this.sqliteRowToItemWithNullConversion(completeSqliteItem);
            await this.sendCommonItem(sqliteCommonItem);
          }
        } else if (newer === 'common') {
          // Fetch complete common item and sync to SQLite
          const completeCommonItem = await this.getCommonItemById(id);
          if (completeCommonItem) {
            const sqliteRow = this.itemToSqliteRowWithNullConversion(completeCommonItem);
            await this.upsertToSqlite(sqliteRow);
          }
        }
        // If equal, no action needed
      } else if (sqliteMinimalItem) {
        // Only in SQLite - sync to common
        const completeSqliteItem = await this.getFromSqliteById(id);
        if (completeSqliteItem) {
          const sqliteCommonItem = this.sqliteRowToItemWithNullConversion(completeSqliteItem);
          await this.sendCommonItem(sqliteCommonItem);
        }
      } else if (commonMinimalItem) {
        // Only in common - sync to SQLite
        const completeCommonItem = await this.getCommonItemById(id);
        if (completeCommonItem) {
          const sqliteRow = this.itemToSqliteRowWithNullConversion(completeCommonItem);
          await this.upsertToSqlite(sqliteRow);
        }
      }
    } catch (error) {
      if (error instanceof ForeignKeyConstraintError) {
        console.warn(
          `Foreign key constraint error for ${this.tableName} item ${id}: ${error.message}. ` +
            `This item will be retried in a future sync when dependencies are available.`
        );
        // Don't re-throw foreign key constraint errors for individual items
      } else {
        // Re-throw non-foreign key constraint errors
        throw error;
      }
    }
  }
}

// ─── SERVER ADAPTER (Cloud/Prisma) ───

export abstract class ServerDatabaseAdapter<
  PrismaRowType extends { id: string; updatedAt: Date } & Record<string, any>,
  PrismaCreateInputType extends { id: string } & Record<string, any>,
  CommonItemType extends CommonItem & { id: string; updatedAt: Date },
  PrismaClientType,
  AdapterName extends ServerAdapterType
> implements
    ServerDatabaseAdapterInterface<
      PrismaRowType,
      PrismaCreateInputType,
      CommonItemType,
      PrismaClientType,
      AdapterName
    >
{
  public readonly _type: AdapterName;
  public readonly prisma: PrismaClientType;
  protected readonly tableName: AdapterName;

  constructor(prisma: PrismaClientType, tableName: AdapterName) {
    this.prisma = prisma;
    this.tableName = tableName;
    this._type = tableName;
  }

  public getTableName(): AdapterName {
    return this.tableName;
  }

  // ─── PUBLIC METHODS ───

  /**
   * Sync data between Prisma and common item format
   * @param direction - Direction of synchronization
   * @param onProgress - Optional callback for progress updates
   */
  public async sync(
    direction: 'toPrisma' | 'toItem' | 'bidirectional' = 'bidirectional',
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    try {
      switch (direction) {
        case 'toPrisma':
          await this.syncToPrisma(onProgress);
          break;
        case 'toItem':
          await this.syncToItem(onProgress);
          break;
        case 'bidirectional':
          await this.syncBidirectional(onProgress);
          break;
        default:
          throw new Error(`Invalid sync direction: ${direction}`);
      }
    } catch (error) {
      console.warn(`Error syncing ${this.tableName}:`, error);
      throw error;
    }
  }

  /**
   * Sync a specific item by ID between Prisma and common item format
   * @param id - The ID of the item to sync
   * @param direction - Direction of synchronization
   */
  public async syncById(
    id: string,
    direction: 'toPrisma' | 'toItem' | 'bidirectional' = 'bidirectional'
  ): Promise<void> {
    try {
      switch (direction) {
        case 'toPrisma':
          await this.syncItemToPrisma(id);
          break;
        case 'toItem':
          await this.syncItemToItem(id);
          break;
        case 'bidirectional':
          await this.syncItemBidirectional(id);
          break;
        default:
          throw new Error(`Invalid sync direction: ${direction}`);
      }
    } catch (error) {
      console.warn(`Error syncing ${this.tableName} item ${id}:`, error);
      throw error;
    }
  }

  /**
   * Sync specific items by ID between Prisma and common item format
   * This method is optimized for rapid incremental updates of frequently changing objects.
   * It uses minimal field fetching for performance and only syncs the specified items.
   *
   * @param itemsByType - Record mapping item types to arrays of item IDs to sync
   * @param direction - Direction of synchronization
   * @param onProgress - Optional progress callback function for real-time updates
   */
  public async syncItems(
    itemsByType: SyncItemsInput,
    direction: 'toPrisma' | 'toItem' | 'bidirectional' = 'bidirectional',
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    try {
      // Get the item IDs for this table type
      const items = itemsByType[this.tableName];
      if (!items || items.length === 0) {
        // This table type is not in the items to sync or has no IDs
        return;
      }

      // Sync each item ID individually
      const totalItems = items.length;
      let completedItems = 0;

      if (onProgress) {
        onProgress(`Starting ${this.tableName} sync...`, 0, totalItems);
      }

      for (const item of items) {
        try {
          // Use the existing syncById method for the specific item
          await this.syncById(item.id, direction);
          completedItems++;

          if (onProgress) {
            onProgress(`Syncing ${this.tableName}...`, completedItems, totalItems);
          }
        } catch (error) {
          console.warn(`Error syncing ${this.tableName} item ${item.id}:`, error);
          // Continue with other items even if one fails
          completedItems++;
          if (onProgress) {
            onProgress(`Syncing ${this.tableName}...`, completedItems, totalItems);
          }
        }
      }
    } catch (error) {
      console.warn(`Error syncing ${this.tableName} items:`, error);
      throw error;
    }
  }

  // ─── PROTECTED ABSTRACT METHODS (MUST BE IMPLEMENTED BY SUBCLASSES) ───

  /**
   * Convert common item to Prisma create/update input format
   *
   * IMPORTANT: This method should return a PrismaCreateInputType that includes:
   * - All required fields for the Prisma model
   * - Proper relationship connections (e.g., user: { connect: { id: userId } })
   * - Nested object creation for related entities (e.g., tags: { connectOrCreate: [...] })
   *
   * The base adapter will call this method before upsertToPrisma, so upsertToPrisma
   * should expect to receive the result of this method, not a raw common item.
   *
   * @param item - Common item data
   * @returns Prisma create/update input format
   */
  public abstract itemToPrismaRow(item: CommonItemType): PrismaCreateInputType;

  /**
   * Convert common item to Prisma create/update input format with null-to-undefined conversion
   * This ensures that any null values in the input item are converted to undefined
   * @param item - Common item data
   * @returns Prisma create/update input format with null values converted to undefined
   */
  protected itemToPrismaRowWithNullConversion(item: CommonItemType): PrismaCreateInputType {
    const convertedItem = convertNullToUndefined(item);
    return this.itemToPrismaRow(convertedItem);
  }

  /**
   * Convert Prisma row to common item format
   * @param row - Prisma row data
   * @returns Common item format
   */
  public abstract prismaRowToItem(row: PrismaRowType): CommonItemType;

  /**
   * Convert Prisma row to common item format with null-to-undefined conversion
   * This is the public method that ensures null values are converted to undefined
   * @param row - Prisma row data
   * @returns Common item format with null values converted to undefined
   */
  protected prismaRowToItemWithNullConversion(row: PrismaRowType): CommonItemType {
    const item = this.prismaRowToItem(row);
    return convertNullToUndefined(item);
  }

  /**
   * Convert minimal Prisma row to minimal common item format
   * @param row - Minimal Prisma row data
   * @returns Minimal common item format
   */
  public abstract minimalPrismaRowToItem(row: PrismaRowType): CommonItemType;

  /**
   * Get the Prisma model instance for this table
   * @returns Prisma model instance
   */
  protected abstract getPrismaModel(): any;

  /**
   * Get all items from PostgreSQL via Prisma
   * @returns Array of Prisma rows
   */
  public abstract getAllFromPrisma(): Promise<PrismaRowType[]>;

  /**
   * Get minimal items from PostgreSQL via Prisma for performance optimization
   * Should return only essential fields: id, updatedAt, and fields needed for deduplication
   * @returns Array of minimal Prisma rows
   */
  public abstract getMinimalFromPrisma(): Promise<
    WithSomeRequired<PrismaRowType, 'id' | 'updatedAt'>[]
  >;

  /**
   * Get item by ID from PostgreSQL via Prisma
   * @param id - Item ID
   * @returns Prisma row or null
   */
  public abstract getFromPrismaById(id: string): Promise<PrismaRowType | null>;

  /**
   * Create or update item in PostgreSQL via Prisma
   *
   * IMPORTANT: This method receives the result of itemToPrismaRow(), which is a
   * PrismaCreateInputType with proper relationship connections and nested objects.
   * Do not call itemToPrismaRow() again in this method - the base adapter handles
   * the conversion flow: CommonItem -> itemToPrismaRow() -> upsertToPrisma().
   *
   * @param row - Prisma create/update input data (result of itemToPrismaRow)
   */
  public abstract upsertToPrisma(row: PrismaCreateInputType): Promise<void>;

  /**
   * Batch upsert to Prisma (parallelized, handles foreign key errors per item)
   */
  protected async upsertListToPrisma(rows: PrismaCreateInputType[]): Promise<void> {
    // Parallelize upserts, but handle ForeignKeyConstraintError for each
    await Promise.all(
      rows.map(async (row) => {
        try {
          await this.upsertToPrisma(row);
        } catch (error) {
          if (error instanceof ForeignKeyConstraintError) {
            console.warn(
              `Foreign key constraint error for ${this.tableName} item ${row.id}: ${error.message}. This item will be retried in a future sync when dependencies are available.`
            );
            // Don't rethrow, just log and skip
          } else {
            throw error;
          }
        }
      })
    );
  }

  /**
   * Get common items from external source (REST layer - not used in server context)
   * @param minimal - Whether to return minimal data only
   * @returns Array of common items
   */
  protected async getCommonItems(_minimal?: boolean): Promise<CommonItemType[]> {
    throw new Error(
      `Server adapter for ${this.tableName} cannot fetch items from client via REST - this would be firewalled`
    );
  }

  /**
   * Get common item by ID from external source (REST layer - not used in server context)
   * @param id - Item ID
   * @param minimal - Whether to return minimal data only
   * @returns Common item or null
   */
  protected async getCommonItemById(
    id: string,
    _minimal?: boolean
  ): Promise<CommonItemType | null> {
    throw new Error(
      `Server adapter for ${this.tableName} cannot fetch item ${id} from client via REST - this would be firewalled`
    );
  }

  /**
   * Send common item to external source (REST layer - not used in server context)
   * @param item - Common item to send
   */
  protected async sendCommonItem(item: CommonItemType): Promise<void> {
    throw new Error(
      `Server adapter for ${this.tableName} cannot send item ${item.id} to client via REST - this would be firewalled`
    );
  }

  /**
   * Send common items to external source (to be implemented by REST layer)
   * @param items - Array of common items to send
   */
  protected async sendCommonItems(
    items: CommonItemType[],
    options: {
      maxConcurrent?: number;
      batchSize?: number;
      delayBetweenBatches?: number;
    } = {}
  ): Promise<void> {
    // Use connection pool implementation for controlled concurrency
    await this.sendCommonItemsWithProgress(items, undefined, options);
  }

  /**
   * Send common items to external source with progress tracking (connection pool)
   *
   * This implementation uses a connection pool to limit concurrent requests
   * and prevent overwhelming the server. Processes items in batches with
   * controlled concurrency.
   *
   * @param items - Array of common items to send
   * @param onProgress - Progress callback function
   * @param options - Connection pool options
   */
  protected async sendCommonItemsWithProgress(
    _items: CommonItemType[],
    _onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void,
    _options?: Partial<AdapterConfig>
  ): Promise<void> {
    // For server adapters, we don't send items to external sources
    // This method is provided for interface compatibility
    throw new Error(
      `Server adapter for ${this.tableName} cannot send items to external sources - this would be firewalled`
    );
  }

  /**
   * Determine which item is newer based on updatedAt timestamp
   * @param prismaItem - Prisma item
   * @param commonItem - Common item
   * @returns 'prisma' | 'common' | 'equal'
   */
  public abstract getNewerItem(
    prismaItem: PrismaRowType,
    commonItem: CommonItemType
  ): 'prisma' | 'common' | 'equal';

  /**
   * Get deduplication strategy for this table
   * @returns Deduplication configuration
   */
  public abstract getDeduplicationStrategy(): DeduplicationStrategy<CommonItemType>;

  /**
   * Provide heavy field configuration (if any) for this adapter.
   * Default implementation returns null indicating no heavy fields.
   */
  public getHeavyFieldConfig(): HeavyFieldConfig<CommonItemType> | null {
    return null;
  }

  public async storeHeavyFields(
    userId: string,
    items: CommonItemType[]
  ): Promise<CommonItemType[]> {
    const config = this.getHeavyFieldConfig();
    if (!config || items.length === 0) {
      return items;
    }

    const { upload } = getHeavyFieldStorageHandlers();
    const payloads: Array<{ item: CommonItemType; payload: Record<string, any> }> = [];

    await Promise.all(
      items.map(async (item) => {
        const payload = await config.createPayload(item);
        if (!payload) {
          return;
        }

        payloads.push({ item, payload });

        const storageKey = config.getStorageKey(userId, item);
        await upload(storageKey, JSON.stringify(payload));

        const supplemental = config.getSupplementalUploads?.(userId, item, payload) ?? [];
        if (supplemental.length) {
          await Promise.all(
            supplemental.map(({ path, contents }) =>
              upload(path, typeof contents === 'string' ? contents : contents.toString())
            )
          );
        }
      })
    );

    if (payloads.length > 0) {
      console.log(`[HeavyFields][${this.getTableName()}] Stored ${payloads.length} items in GCS`, {
        userId,
        heavyFields: config.fields
      });
    }

    return items.map((item) => config.stripHeavyFields(item));
  }

  public async hydrateHeavyFields(
    userId: string,
    items: CommonItemType[],
    options: { includeHeavy: boolean }
  ): Promise<CommonItemType[]> {
    const config = this.getHeavyFieldConfig();
    if (!config || items.length === 0) {
      return items;
    }

    if (!options.includeHeavy) {
      return items.map((item) => config.stripHeavyFields(item));
    }

    const { download } = getHeavyFieldStorageHandlers();

    return Promise.all(
      items.map(async (item) => {
        const storageKey = config.getStorageKey(userId, item);
        try {
          const payload = await download<Record<string, any>>(storageKey);
          return config.applyPayload(config.stripHeavyFields(item), payload);
        } catch (error) {
          console.warn(
            `[HeavyFields][${this.getTableName()}] Failed to retrieve blob for item ${(item as any).id}`,
            {
              storageKey,
              error
            }
          );
          return config.stripHeavyFields(item);
        }
      })
    );
  }

  public stripHeavyFields(items: CommonItemType[]): CommonItemType[] {
    const config = this.getHeavyFieldConfig();
    if (!config || items.length === 0) {
      return items;
    }
    return items.map((item) => config.stripHeavyFields(item));
  }

  /**
   * Get deduplication key for an item
   * @param item - Common item
   * @returns Deduplication key
   */
  protected getDeduplicationKey(item: CommonItemType): string {
    const strategy = this.getDeduplicationStrategy();
    if (strategy.type === 'none') {
      return item.id;
    }
    return strategy.getKey(item);
  }

  /**
   * Group items by their deduplication key
   * @param items - Array of items to group
   * @returns Map of deduplication key to array of items
   */
  protected groupItemsByDeduplicationKey(items: CommonItemType[]): Map<string, CommonItemType[]> {
    const groups = new Map<string, CommonItemType[]>();
    for (const item of items) {
      const key = this.getDeduplicationKey(item);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    }
    return groups;
  }

  /**
   * Get the latest item from a group based on updatedAt timestamp
   * @param items - Array of items to compare
   * @returns The item with the latest updatedAt timestamp
   */
  protected getLatestItem(items: CommonItemType[]): CommonItemType {
    if (items.length === 0) {
      throw new Error('Cannot get latest item from empty array');
    }
    return items.reduce((latest, current) => {
      return current.updatedAt.getTime() > latest.updatedAt.getTime() ? current : latest;
    });
  }

  /**
   * Deduplicate items by keeping only the latest item per deduplication key
   * @param items - Array of items to deduplicate
   * @returns Array of deduplicated items
   */
  protected deduplicateItems(items: CommonItemType[]): CommonItemType[] {
    const groups = this.groupItemsByDeduplicationKey(items);
    const deduplicated: CommonItemType[] = [];

    for (const [_key, groupItems] of groups) {
      if (groupItems.length > 0) {
        if (groupItems.length > 1) {
          console.log(
            `[${this.tableName}] Deduplicating group "${_key}": ${groupItems.length} items (from different sources)`
          );
        }

        const latestItem = this.getLatestItem(groupItems);
        deduplicated.push(latestItem);
      }
    }

    return deduplicated;
  }

  // ─── PROTECTED CONCRETE METHODS ───

  /**
   * Sync all data from common items to Prisma
   */
  protected async syncToPrisma(
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    const commonItems = await this.getCommonItems();
    const totalItems = commonItems.length;
    const errors: Array<{ itemId: string; error: unknown }> = [];

    if (onProgress) {
      onProgress(`Syncing ${this.tableName} items to database...`, 0, totalItems);
    }

    const itemsToSendToCommon: CommonItemType[] = [];
    const itemsToUpsertToPrisma: PrismaCreateInputType[] = [];

    for (let i = 0; i < commonItems.length; i++) {
      const item = commonItems[i];
      try {
        const prismaRow = this.itemToPrismaRowWithNullConversion(item);
        itemsToUpsertToPrisma.push(prismaRow);
        if (onProgress) {
          onProgress(`Syncing ${this.tableName} items to database...`, i + 1, totalItems);
        }
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          console.warn(
            `Foreign key constraint error for ${this.tableName} item ${item.id}: ${error.message}. ` +
              `This item will be retried in a future sync when dependencies are available.`
          );
          errors.push({
            itemId: item.id,
            error: {
              code: error.code,
              message: error.message,
              dependentTable: error.dependentTable,
              dependentId: error.dependentId
            }
          });
          if (onProgress) {
            onProgress(`Syncing ${this.tableName} items to database...`, i + 1, totalItems);
          }
        } else {
          // Re-throw non-foreign key constraint errors
          throw error;
        }
      }
    }

    // Batch send to common
    if (itemsToSendToCommon.length > 0) {
      await this.sendCommonItems(itemsToSendToCommon);
    }

    // Batch upsert to Prisma
    if (itemsToUpsertToPrisma.length > 0) {
      await this.upsertListToPrisma(itemsToUpsertToPrisma);
    }

    if (errors.length > 0) {
      console.warn(
        `${this.tableName} sync completed with ${errors.length} foreign key constraint errors. ` +
          `Failed items will be retried in future syncs when dependencies are available.`
      );
    }
  }

  /**
   * Sync all data from Prisma to common items
   */
  protected async syncToItem(
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    const prismaItems = await this.getAllFromPrisma();
    const commonItems = prismaItems.map((row) => this.prismaRowToItemWithNullConversion(row));
    const totalItems = commonItems.length;

    if (onProgress) {
      onProgress(`Syncing ${this.tableName} items to cloud...`, 0, totalItems);
    }

    if (commonItems.length > 0) {
      await this.sendCommonItems(commonItems);
      if (onProgress) {
        onProgress(`Syncing ${this.tableName} items to cloud...`, totalItems, totalItems);
      }
    }
  }

  /**
   * Bidirectional sync with conflict resolution and deduplication
   */
  protected async syncBidirectional(
    onProgress?: (operation: string, itemsComplete: number, totalItems: number) => void
  ): Promise<void> {
    if (onProgress) {
      // Use (0, 0) to indicate analysis phase where we don't yet know total items
      // This allows the UI to show meaningful progress even during initial analysis
      // The aggregate progress system will handle this specially and not advance progress
      onProgress(`Analyzing ${this.tableName} data...`, 0, 0);
    }

    // STAGE 1: Get minimal items for analysis (performance optimization)
    const prismaMinimalItems = await this.getMinimalFromPrisma();
    const commonMinimalItems = await this.getCommonItems(true);

    const prismaMinimalMap = new Map(
      prismaMinimalItems.map((row) => [
        row.id,
        this.minimalPrismaRowToItem(row as unknown as PrismaRowType)
      ])
    );
    const commonMinimalMap = new Map(commonMinimalItems.map((item) => [item.id, item]));

    // Apply deduplication to the combined minimal items
    // Note: We need to convert minimal items to common items for deduplication
    const allMinimalItems = [
      ...Array.from(prismaMinimalMap.values()).map(
        (minimal) => ({ ...minimal, createdAt: new Date(0) }) as CommonItemType
      ),
      ...commonMinimalMap.values()
    ];
    const deduplicatedMinimalItems = this.deduplicateItems(allMinimalItems);

    const itemsToSendToCommon: CommonItemType[] = [];
    const itemsToUpsertToPrisma: PrismaCreateInputType[] = [];
    let conflictsResolved = 0;
    let itemsOnlyInPrisma = 0;
    let itemsOnlyInCommon = 0;

    // STAGE 2: Process deduplicated items and fetch complete data as needed
    for (const minimalItem of deduplicatedMinimalItems) {
      const prismaMinimalItem = prismaMinimalMap.get(minimalItem.id);
      const commonMinimalItem = commonMinimalMap.get(minimalItem.id);

      if (prismaMinimalItem && commonMinimalItem) {
        // Item exists in both - resolve conflicts
        conflictsResolved++;
        const newer = this.getNewerItem(
          { ...prismaMinimalItem, createdAt: new Date(0) } as unknown as PrismaRowType,
          commonMinimalItem
        );
        if (newer === 'prisma') {
          // Fetch complete Prisma item
          const completePrismaItem = await this.getFromPrismaById(minimalItem.id);
          if (completePrismaItem) {
            const prismaCommonItem = this.prismaRowToItem(completePrismaItem);
            itemsToSendToCommon.push(prismaCommonItem);
          }
        } else if (newer === 'common') {
          // Fetch complete common item
          const completeCommonItem = await this.getCommonItemById(minimalItem.id);
          if (completeCommonItem) {
            const prismaRow = this.itemToPrismaRowWithNullConversion(completeCommonItem);
            itemsToUpsertToPrisma.push(prismaRow);
          }
        }
        // If equal, no action needed
      } else if (prismaMinimalItem) {
        // Only in Prisma - sync to common
        itemsOnlyInPrisma++;
        // Fetch complete Prisma item
        const completePrismaItem = await this.getFromPrismaById(minimalItem.id);
        if (completePrismaItem) {
          const prismaCommonItem = this.prismaRowToItemWithNullConversion(completePrismaItem);
          itemsToSendToCommon.push(prismaCommonItem);
        }
      } else if (commonMinimalItem) {
        // Only in common - sync to Prisma
        itemsOnlyInCommon++;
        // Fetch complete common item
        const completeCommonItem = await this.getCommonItemById(minimalItem.id);
        if (completeCommonItem) {
          const prismaRow = this.itemToPrismaRowWithNullConversion(completeCommonItem);
          itemsToUpsertToPrisma.push(prismaRow);
        }
      }
    }

    console.log(
      `[${this.tableName}] Starting bidirectional sync: ${deduplicatedMinimalItems.length} total items`
    );
    console.log(
      `[${this.tableName}] Conflicts to resolve: ${conflictsResolved}, Only in Prisma: ${itemsOnlyInPrisma}, Only in Common: ${itemsOnlyInCommon}`
    );

    let syncedToCommon = 0;
    let syncedToPrisma = 0;
    let deferredToCommon = 0;
    let deferredToPrisma = 0;

    // Batch send to common
    if (itemsToSendToCommon.length > 0) {
      if (onProgress) {
        onProgress(`Syncing ${this.tableName} to cloud...`, 0, itemsToSendToCommon.length);
      }
      try {
        await this.sendCommonItems(itemsToSendToCommon);
        syncedToCommon = itemsToSendToCommon.length;
        if (onProgress) {
          onProgress(
            `Syncing ${this.tableName} to cloud...`,
            itemsToSendToCommon.length,
            itemsToSendToCommon.length
          );
        }
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          deferredToCommon = itemsToSendToCommon.length;
          console.warn(
            `[${this.tableName}] All ${itemsToSendToCommon.length} items deferred to common due to foreign key constraint`
          );
          if (onProgress) {
            onProgress(
              `Syncing ${this.tableName} to cloud...`,
              itemsToSendToCommon.length,
              itemsToSendToCommon.length
            );
          }
        } else {
          throw error;
        }
      }
    }

    // Batch upsert to Prisma
    if (itemsToUpsertToPrisma.length > 0) {
      if (onProgress) {
        onProgress(`Syncing ${this.tableName} to database...`, 0, itemsToUpsertToPrisma.length);
      }
      try {
        await this.upsertListToPrisma(itemsToUpsertToPrisma);
        syncedToPrisma = itemsToUpsertToPrisma.length;
        if (onProgress) {
          onProgress(
            `Syncing ${this.tableName} to database...`,
            itemsToUpsertToPrisma.length,
            itemsToUpsertToPrisma.length
          );
        }
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          deferredToPrisma = itemsToUpsertToPrisma.length;
          console.warn(
            `[${this.tableName}] All ${itemsToUpsertToPrisma.length} items deferred to Prisma due to foreign key constraint`
          );
          if (onProgress) {
            onProgress(
              `Syncing ${this.tableName} to database...`,
              itemsToUpsertToPrisma.length,
              itemsToUpsertToPrisma.length
            );
          }
        } else {
          throw error;
        }
      }
    }

    console.log(
      `[${this.tableName}] Sync completed: ${syncedToCommon} synced to common, ${syncedToPrisma} synced to Prisma, ${deferredToCommon} deferred to common, ${deferredToPrisma} deferred to Prisma`
    );

    // Log sync status summary
    const totalSynced = syncedToCommon + syncedToPrisma;
    const totalDeferred = deferredToCommon + deferredToPrisma;
    const isFullySynced = totalSynced === 0 && totalDeferred === 0;

    if (isFullySynced) {
      console.log(`[${this.tableName}] ✅ Databases are in sync (no changes needed)`);
    } else if (totalDeferred > 0) {
      console.log(
        `[${this.tableName}] ⚠️  Sync partially complete (${totalDeferred} items deferred)`
      );
    } else {
      console.log(`[${this.tableName}] ✅ Sync complete (${totalSynced} items synced)`);
    }
  }

  /**
   * Sync specific item to Prisma
   */
  protected async syncItemToPrisma(id: string): Promise<void> {
    const commonItem = await this.getCommonItemById(id);
    if (commonItem) {
      try {
        const prismaRow = this.itemToPrismaRowWithNullConversion(commonItem);
        await this.upsertToPrisma(prismaRow);
      } catch (error) {
        if (error instanceof ForeignKeyConstraintError) {
          console.warn(
            `Foreign key constraint error for ${this.tableName} item ${id}: ${error.message}. ` +
              `This item will be retried in a future sync when dependencies are available.`
          );
          // Don't re-throw foreign key constraint errors for individual items
        } else {
          // Re-throw non-foreign key constraint errors
          throw error;
        }
      }
    }
  }

  /**
   * Sync specific item to common
   */
  protected async syncItemToItem(id: string): Promise<void> {
    const prismaItem = await this.getFromPrismaById(id);
    if (prismaItem) {
      const commonItem = this.prismaRowToItemWithNullConversion(prismaItem);
      await this.sendCommonItem(commonItem);
    }
  }

  /**
   * Bidirectional sync for specific item
   */
  protected async syncItemBidirectional(id: string): Promise<void> {
    const prismaItem = await this.getFromPrismaById(id);
    const commonItem = await this.getCommonItemById(id);

    // let syncDirection: 'prisma-to-common' | 'common-to-prisma' | 'none' | 'deferred' = 'none';
    // let deferredReason = '';

    try {
      if (prismaItem && commonItem) {
        const prismaCommon = this.prismaRowToItemWithNullConversion(prismaItem);

        const newer = this.getNewerItem(prismaItem, commonItem);
        if (newer === 'prisma') {
          await this.sendCommonItem(prismaCommon);
        } else if (newer === 'common') {
          const prismaRow = this.itemToPrismaRowWithNullConversion(commonItem);
          await this.upsertToPrisma(prismaRow);
        }
        // If equal, no action needed
      } else if (prismaItem) {
        await this.syncItemToItem(id);
      } else if (commonItem) {
        await this.syncItemToPrisma(id);
      }
    } catch (error) {
      if (error instanceof ForeignKeyConstraintError) {
        console.warn(
          `Foreign key constraint error for ${this.tableName} item ${id}: ${error.message}. ` +
            `This item will be retried in a future sync when dependencies are available.`
        );
        // Don't re-throw foreign key constraint errors for individual items
      } else {
        // Re-throw non-foreign key constraint errors
        throw error;
      }
    }
  }

  // ─── SERVER ROUTE HELPERS ───

  /**
   * Fetch items by IDs and optionally hydrate heavy fields
   * Helper for batch retrieval endpoints
   */
  public async getBatchItems(
    userId: string,
    ids: string[],
    options: { minimal?: boolean; includeHeavy?: boolean }
  ): Promise<CommonItemType[]> {
    const awaitedItems = await Promise.all(
      ids.map(async (id) => {
        try {
          const row = await this.getFromPrismaById(id);
          if (!row) return Promise.resolve(null);
          return Promise.resolve(this.prismaRowToItem(row));
        } catch {
          return Promise.resolve(null);
        }
      })
    );

    const items = awaitedItems.filter((t): t is Awaited<CommonItemType> => t !== null && !!t);

    const includeHeavy = options.includeHeavy ?? !options.minimal;
    const hydratedItems = await this.hydrateHeavyFields(userId, items, { includeHeavy });
    return includeHeavy ? hydratedItems : this.stripHeavyFields(hydratedItems);
  }

  /**
   * Update a single item (store heavy fields, convert to Prisma, upsert)
   * Helper for PUT/PATCH endpoints
   */
  public async updateItem(userId: string, item: CommonItemType): Promise<void> {
    const [preparedItem] = await this.storeHeavyFields(userId, [item]);
    const itemToConvert = preparedItem || item;
    const prismaRow = this.itemToPrismaRow(itemToConvert);
    await this.upsertToPrisma(prismaRow);
  }

  /**
   * Get a single item by ID and optionally hydrate heavy fields
   * Helper for GET endpoints
   */
  public async getItemById(
    userId: string,
    id: string,
    options: { minimal?: boolean; includeHeavy?: boolean }
  ): Promise<CommonItemType | null> {
    const row = await this.getFromPrismaById(id);

    if (!row) return null;

    const commonItem = options.minimal
      ? this.minimalPrismaRowToItem(row)
      : this.prismaRowToItem(row);

    const includeHeavy = options.includeHeavy ?? !options.minimal;
    const [hydratedItem] = await this.hydrateHeavyFields(userId, [commonItem], { includeHeavy });

    return hydratedItem || (includeHeavy ? commonItem : this.stripHeavyFields([commonItem])[0]);
  }
}

// ─── UTILITY FUNCTIONS ───

/**
 * Compare two dates and return which is newer
 */
export function compareDates(date1: Date, date2: Date): 'first' | 'second' | 'equal' {
  const time1 = date1.getTime();
  const time2 = date2.getTime();

  if (time1 > time2) return 'first';
  if (time1 < time2) return 'second';
  return 'equal';
}

/**
 * Convert string date to Date object safely
 */
export function parseDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * Convert Date to ISO string safely
 */
export function formatDate(date: Date | null | undefined): string | null {
  if (!date) return null;
  return date.toISOString();
}
